// https://github.com/iHongRen/WebSocket-Vue/blob/main/userWebSocket.js
// 导入Vue中的ref功能，用于创建响应式引用对象
import { ref } from 'vue';

// 默认配置选项，包括WebSocket的URL、心跳数据、心跳间隔等
const DEFAULT_OPTIONS = {
  url: '', // WebSocket服务器的URL地址
  heartBeatData: '', // 心跳检测发送的数据
  heartBeatInterval: 60 * 1000, // 心跳检测的时间间隔（毫秒）
  reconnectInterval: 5000, // 重连时间间隔（毫秒）
  maxReconnectAttempts: 10, // 最大重连尝试次数
};

// WebSocket的状态枚举，用于描述连接的不同状态
export const SocketStatus = {
  Connecting: '正在连接...', // 正在尝试连接
  Connected: '连接已建立', // 连接成功
  Disconnecting: '连接正在关闭', // 正在关闭连接
  Disconnected: '连接已断开', // 连接已断开或未建立
};

// 正常关闭WebSocket的代码
const SocketCloseCode = 1000;

// 定义一个组合式API函数，用于处理WebSocket连接和管理
export default function useWebSocket(options = {}) {
  // 创建状态对象，包含WebSocket实例、重连计数、定时器等
  const state: any = {
    options: { ...DEFAULT_OPTIONS, ...options }, // 合并默认配置与传入配置
    socket: null, // WebSocket实例
    reconnectAttempts: 0, // 当前重连尝试次数
    reconnectTimeout: null, // 重连定时器
    heartBetaSendTimer: null, // 心跳发送定时器
    heartBetaTimeoutTimer: null, // 心跳超时定时器
  };

  // 定义连接状态、消息和错误的响应式引用
  const status = ref(SocketStatus.Disconnected); // 连接状态
  const message = ref(null); // 接收的消息
  const error = ref(null); // 错误信息

  // 连接WebSocket的函数
  const connect = () => {
    // 在连接前先断开已存在的连接
    disconnect();

    // 设置状态为正在连接
    status.value = SocketStatus.Connecting;
    // 如果浏览器不在线，则延迟设置状态为断开，并退出
    if (!window.navigator.onLine) {
      setTimeout(() => {
        status.value = SocketStatus.Disconnected;
      }, 500);
      return;
    }

    // 创建WebSocket实例并监听事件
    state.socket = new WebSocket(state.options.url);
    state.socket.onopen = handleOpen; // 监听打开事件
    state.socket.onmessage = handleMessage; // 监听消息事件
    state.socket.onclose = handleClose; // 监听关闭事件
    state.socket.onerror = handleError; // 监听错误事件
  };

  // 处理打开事件的函数
  const handleOpen = (openEvent) => {
    console.log('socket连接:', openEvent);
    state.reconnectAttempts = 0; // 重置重连计数
    status.value = SocketStatus.Connected; // 设置状态为已连接
    error.value = null; // 清除错误
    startHeartBeat(); // 开始心跳检测
  };

  // 处理消息事件的函数
  const handleMessage = (msgEvent) => {
    console.log('socket消息:', msgEvent);
    // 收到任何数据，重新开始心跳
    startHeartBeat();

    // 解析接收到的消息
    const { data } = msgEvent;
    const msg = JSON.parse(JSON.stringify({ msgEventData: data }));

    // 检查是否为心跳数据
    if (+msg.msg_id === 0) {
      return;
    }
    // 更新消息状态
    message.value = msg;
  };

  // 处理关闭事件的函数
  const handleClose = (closeEvent) => {
    console.log('socket关闭:', closeEvent);
    status.value = SocketStatus.Disconnected; // 设置状态为断开
    // 如果非正常关闭，则尝试重连
    if (closeEvent.code !== SocketCloseCode) {
      reconnect();
    }
  };

  // 处理错误事件的函数
  const handleError = (errEvent) => {
    console.log('socket报错:', errEvent);
    status.value = SocketStatus.Disconnected; // 设置状态为断开
    error.value = errEvent; // 更新错误信息
    // 连接失败，尝试重连
    reconnect();
  };

  // 断开WebSocket连接的函数
  const disconnect = () => {
    if (state.socket && (state.socket.OPEN || state.socket.CONNECTING)) {
      console.log('socket断开连接');
      status.value = SocketStatus.Disconnecting; // 设置状态为正在关闭
      // 清除所有监听器
      state.socket.onmessage = null;
      state.socket.onerror = null;
      state.socket.onclose = null;
      // 发送关闭帧给服务端
      state.socket.close(SocketCloseCode, 'normal closure');
      status.value = SocketStatus.Disconnected; // 设置状态为断开
      state.socket = null; // 清空WebSocket实例
    }
    stopHeartBeat(); // 停止心跳检测
    stopReconnect(); // 停止重连尝试
  };

  // 开始心跳检测的函数
  const startHeartBeat = () => {
    stopHeartBeat(); // 先停止现有心跳检测
    onHeartBeat(() => {
      if (status.value === SocketStatus.Connected) {
        state.socket.send(state.options.heartBeatData); // 发送心跳数据
        console.log('socket心跳发送:', state.options.heartBeatData);
      }
    });
  };

  // 执行心跳检测的函数
  const onHeartBeat = (callback) => {
    state.heartBetaSendTimer = setTimeout(() => {
      callback && callback(); // 执行回调函数
      state.heartBetaTimeoutTimer = setTimeout(() => {
        // 心跳超时，关闭WebSocket并尝试重连
        state.socket.close(4444, 'heart timeout');
      }, state.options.heartBeatInterval);
    }, state.options.heartBeatInterval);
  };

  // 停止心跳检测的函数
  const stopHeartBeat = () => {
    state.heartBetaSendTimer && clearTimeout(state.heartBetaSendTimer);
    state.heartBetaTimeoutTimer && clearTimeout(state.heartBetaTimeoutTimer);
  };

  // 尝试重连的函数
  const reconnect = () => {
    if (status.value === SocketStatus.Connected || status.value === SocketStatus.Connecting) {
      return;
    }
    stopHeartBeat(); // 停止心跳检测
    if (state.reconnectAttempts < state.options.maxReconnectAttempts) {
      console.log('socket重连:', state.reconnectAttempts);
      // 计算重连间隔时间
      const interval = Math.max(state.options.reconnectInterval, state.reconnectAttempts * 1000);
      console.log('间隔时间：', interval);
      state.reconnectTimeout = setTimeout(() => {
        if (status.value !== SocketStatus.Connected && status.value !== SocketStatus.Connecting) {
          connect(); // 尝试重新连接
        }
      }, interval);
      state.reconnectAttempts += 1; // 增加重连计数
    } else {
      status.value = SocketStatus.Disconnected; // 达到最大重连次数，设置状态为断开
      stopReconnect(); // 停止重连尝试
    }
  };

  // 停止重连尝试的函数
  const stopReconnect = () => {
    state.reconnectTimeout && clearTimeout(state.reconnectTimeout);
  };

  // 返回组合式API的公共接口
  return {
    status, // 连接状态
    message, // 接收的消息
    error, // 错误信息
    connect, // 连接函数
    disconnect, // 断开连接函数
  };
}
